import createNamedContext from 'utils/createNamedContext';
import * as React from 'react';

type LoadingContextProps = {
  single: boolean;
  batch: Record<string, boolean>;
  batchLoading: boolean;
  allLoading: boolean;
  setSingle: React.Dispatch<React.SetStateAction<boolean>>;
  setBatchSingle: (key: string, value: boolean) => void;
  getBatchSingle: (key: string) => boolean;
};

const LoadingContext = createNamedContext<LoadingContextProps>('LoadingContext', null!);

export const useLoadingContext = () => {
  const context = React.useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoadingContext must be used within a LoadingProvider');
  }
  return context;
};

export const LoadingProvider = React.memo((props: { children: React.ReactNode }) => {
  const { children } = props;
  const [single, setSingle] = React.useState(true);
  const [batch, setBatch] = React.useState<Record<string, boolean>>({});
  const batchLoading = React.useMemo(() => {
    return Object.values(batch).some((item) => item);
  }, [batch]);

  const allLoading = React.useMemo(() => {
    return single || batchLoading;
  }, [single, batchLoading]);

  const getBatchSingle = React.useCallback(
    (key: string) => {
      return batch[key] ?? false;
    },
    [batch],
  );

  const setBatchSingle = React.useCallback((key: string, value: boolean) => {
    setBatch((prev) => {
      return {
        ...prev,
        [key]: value,
      };
    });
  }, []);

  return (
    <LoadingContext.Provider
      value={{ single, batch, batchLoading, setSingle, setBatchSingle, allLoading, getBatchSingle }}
    >
      {children}
    </LoadingContext.Provider>
  );
});

export const SingleWrap = React.memo((props: { children: React.ReactNode }) => {
  const { children } = props;
  const { single } = useLoadingContext();
  if (single) return null;
  return <>{children}</>;
});
